/**
 * SDK session token — the ONE token that authenticates BOTH the signaling socket
 * AND sdk-server REST (chat/presence/push). Obtained by EXCHANGING the app's
 * inMeet JWT at sdk-server's mint endpoint (`POST /api/v1/auth/token`), so the SDK
 * stack no longer depends on the inMeet HS256 token directly.
 *
 * Phase 2 of the token-issuer migration (myHelp/sdk-session-token-mint-plan.md).
 *
 * ROBUST BY DESIGN: every consumer that needs a token can fall back to the raw
 * inMeet JWT if the exchange fails (sdk-server still accepts HS256 while the
 * migration flag is on). So swapping to the SDK token can never *reduce*
 * availability — worst case is the pre-swap behaviour.
 */

import { ensureFreshAccessToken } from './auth-refresh'

const SDK_API_URL = (import.meta.env.VITE_SDK_API_URL as string | undefined)?.replace(/\/+$/, '') || ''

/** The app's current inMeet/SecureID Bearer (the exchange input). */
export function getAppBearer(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const s = parsed?.session
    if (s?.expiresAt && s.expiresAt <= Date.now()) return ''
    return s?.accessToken || ''
  } catch {
    return ''
  }
}

/**
 * The bearer, renewing it first if it has aged out.
 *
 * The app token rotates every 15 minutes now. `getAppBearer()` returns '' past
 * the expiry, and a tab that wakes between the expiry and the refresher's next
 * tick would otherwise mint no SDK token and drop the socket — a reconnect
 * storm caused purely by timing.
 */
async function getFreshAppBearer(): Promise<string> {
  const bearer = getAppBearer()
  if (bearer) return bearer
  return ensureFreshAccessToken()
}

/**
 * The `sub` claim, read without verifying — used only to notice that a
 * DIFFERENT USER is signed in. Verification is sdk-server's job.
 */
function subjectOf(jwt: string): string {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return ''
    // atob yields BYTES, not text — decode them as UTF-8 so a non-ASCII claim
    // elsewhere in the payload cannot corrupt the parse.
    const bin = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
    return (JSON.parse(json) as { sub?: string }).sub || ''
  } catch {
    return ''
  }
}

interface Cached {
  token: string
  /** epoch ms after which we refresh (a minute before real expiry). */
  refreshAt: number
  /**
   * The `sub` of the bearer this was minted from.
   *
   * Deliberately the subject, NOT the raw bearer string. This used to compare
   * whole tokens, which was equivalent while the app token lived 24h — now it
   * rotates every 15 minutes, so comparing strings would throw away a still-
   * valid SDK token (1h TTL) on every rotation and re-mint four times an hour.
   * What actually invalidates an SDK token is a different user, and that is
   * what `sub` captures.
   */
  fromSubject: string
}
let cache: Cached | null = null
let inflight: Promise<string> | null = null

/**
 * Return a valid SDK session token, minting/refreshing as needed. Resolves to
 * `''` if there's no app bearer or the exchange fails (callers then fall back to
 * the app bearer). Never throws.
 */
export async function getSdkSessionToken(): Promise<string> {
  if (!SDK_API_URL) return ''
  const bearer = await getFreshAppBearer()
  if (!bearer) return ''

  const subject = subjectOf(bearer)
  const now = Date.now()
  if (cache && cache.fromSubject === subject && now < cache.refreshAt) return cache.token
  if (inflight) return inflight

  inflight = (async () => {
    try {
      const res = await fetch(`${SDK_API_URL}/api/v1/auth/token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (!res.ok) return ''
      const data = (await res.json()) as { accessToken?: string; expiresIn?: number }
      const token = data.accessToken || ''
      if (token) {
        const ttl = typeof data.expiresIn === 'number' ? data.expiresIn : 3600
        cache = { token, refreshAt: Date.now() + Math.max(ttl - 60, 30) * 1000, fromSubject: subject }
      }
      return token
    } catch {
      return ''
    } finally {
      inflight = null
    }
  })()
  return inflight
}

/**
 * The token to use as the REST Bearer: the SDK session token when available,
 * else the raw app bearer (accepted while the HS256 migration flag is on).
 */
export async function getSdkOrAppToken(): Promise<string> {
  const sdk = await getSdkSessionToken()
  // The fallback must be a FRESH bearer too — handing sdk-server one that just
  // expired would 401 exactly when the SDK mint was already unavailable.
  return sdk || (await getFreshAppBearer())
}

/** Drop the cached SDK token (e.g. on logout / auth change). */
export function clearSdkSessionToken(): void {
  cache = null
  inflight = null
}
